import { Refresh as RefreshIcon } from '@mui/icons-material';
import { CircularProgress, Collapse } from '@mui/material';
import { SendDataFormContext } from 'js/components/sendData/SendDataForm';
import {
  GET_CODE_CONTAINERS_REQUEST,
  GET_PUBLIC_CODE_CONTAINERS_REQUEST,
  GET_TRANSFORMED_SAMPLES_REQUEST,
} from 'js/constants/actionTypes';
import {
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
} from 'js/constants/schemaEditor';
import { debounce, filter, map } from 'lodash';
import flowTypes from 'new/components/flows/types/flowTypes';
import { TransformType } from 'new/components/ReusableTransform/types';
import React from 'react';
import { connect } from 'react-redux';
import Constants from '../../Constants';
import TransformsUtils from '../../lib/TransformsUtils';
import Tooltip from '../elements/mui/Tooltip';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  recipeBuilderSelectStyles,
  SelectInput,
  SelectOption,
} from '../elements/rselect/ReactSelectUtils';
import UnsortedSelect from '../elements/rselect/UnsortedSelect';
import { TransformCodeMonacoEditor } from '../elements/schema-editor/schema-inspector/transforms/TransformCodeMonacoEditor';
import { Base64 } from 'js-base64';

const AGGREGATION_WINDOW_OPTIONS = [
  {
    label: 'Default: Source Ingestion Cycle',
    value: 'default',
    description:
      'Records are bucketed by ingestion cycle boundaries, corresponding to each source run. Use this option to prevent aggregation computations from spanning across ingestion cycles.',
  },
  {
    label: 'Topic Memory: Across Ingestion Cycles',
    value: 'topic',
    description:
      'Ingestion cycle boundaries are ignored during record aggregation, spanning all records retained in topic memory. Note that the platform does not store records indefinitely, and computations do not include records processed by the parent Nexset.',
  },
];

class TransformCodeRecipeGroup extends React.PureComponent {
  static contextType = SendDataFormContext;
  render() {
    let {
      type: recipeGroupType,
      data: { type, code, codeLanguage, transformId, options },
      transforms,
      languageOptions,
      transformOptions,
      publicTransformOptions,
      isReadOnly,
      isExpanded,
      isLoadingReusableTransforms,
      onLoadReusableTransforms,
      hasOtherTxBlocks,
    } = this.props;

    const { flowType } = this.context;

    // Disable reusable transform options if other Tx blocks exist
    const disableReusableTransforms = hasOtherTxBlocks && type === 'custom_code';
    const reusableTransformsTooltip = disableReusableTransforms
      ? 'Reusable Transforms can only be used as the sole transform block. Remove other transform blocks first.'
      : null;

    const codeTypeOptions = [
      {
        label: 'Write Custom Code',
        options: languageOptions,
      },
      {
        label: 'Your Reusable Transforms',
        options: transformOptions.map((opt) => ({
          ...opt,
          isDisabled: disableReusableTransforms,
          tooltip: reusableTransformsTooltip,
        })),
      },
      {
        label: 'Public Reusable Transforms',
        options: publicTransformOptions.map((opt) => ({
          ...opt,
          isDisabled: disableReusableTransforms,
          tooltip: reusableTransformsTooltip,
        })),
      },
    ];

    const isCustomCode = type === 'custom_code';
    const selectedCodeTypeOption = isCustomCode
      ? getCurrentSelectedOption(languageOptions, codeLanguage)
      : getCurrentSelectedOption(transformOptions, transformId) ||
        getCurrentSelectedOption(publicTransformOptions, transformId);
    const transform = transforms[transformId];
    const selectedAggregationWindowOption = getCurrentSelectedOption(
      AGGREGATION_WINDOW_OPTIONS,
      options?.aggregation_window || AGGREGATION_WINDOW_OPTIONS[0].value,
    );

    if (transform) {
      if (transform.ai_function_type && typeof transform.code === 'string') {
        if (transform.code_encoding === 'base64') {
          code = Base64.decode(transform.code);
        }
      } else if (!isCustomCode) {
        const decodedTransform = TransformsUtils.convertTransformToCode(transform.code);
        code = decodedTransform.code;
        codeLanguage = decodedTransform.codeLanguage;
      }
    }

    const editorMode =
      Constants.EditorModeByLanguage[codeLanguage || Constants.CodeLanguages.JOLT].name;

    return (
      <Collapse in={isExpanded}>
        <div>
          {isReadOnly ? (
            <>
              {selectedCodeTypeOption && !isCustomCode && <>Reusable Transform:&nbsp;</>}
              {recipeGroupType === RECIPE_GROUP_TRANSFORMS_AGGREGATE &&
                flowType !== flowTypes.spark && (
                  <div className="transform-code-type">
                    Aggregation Window: {selectedAggregationWindowOption?.label}
                  </div>
                )}
            </>
          ) : (
            <>
              <div className="transform-code-type-container">
                {this._renderSelect(
                  codeTypeOptions,
                  selectedCodeTypeOption,
                  this._handleCodeTypeChanged,
                )}
                {recipeGroupType === RECIPE_GROUP_TRANSFORMS_CODE && (
                  <>
                    {isLoadingReusableTransforms ? (
                      <CircularProgress
                        color="primary"
                        size={22}
                        style={{
                          marginLeft: 11,
                          marginRight: 9,
                        }}
                      />
                    ) : (
                      <Tooltip title="Reload Reusable Transforms" arrow={true}>
                        <RefreshIcon
                          fontSize="large"
                          style={{
                            width: 28,
                            height: 28,
                            marginLeft: 'var(--spacing-md)',
                            marginRight: 6,
                            cursor: 'pointer',
                            fill: '#aaa',
                          }}
                          onClick={onLoadReusableTransforms}
                        />
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
              {recipeGroupType === RECIPE_GROUP_TRANSFORMS_AGGREGATE &&
                flowType !== flowTypes.spark && (
                  <div className="transform-code-type-container">
                    <label>Aggregation Window:</label>
                    {this._renderSelect(
                      AGGREGATION_WINDOW_OPTIONS,
                      selectedAggregationWindowOption,
                      this._handleAggregationWindowChanged,
                    )}
                  </div>
                )}
            </>
          )}
        </div>
        <TransformCodeMonacoEditor
          value={code}
          height={360}
          theme="light"
          language={editorMode}
          options={{ readOnly: isReadOnly || !isCustomCode }}
          onChange={debounce(this._handleCodeChanged, 200)}
        />
      </Collapse>
    );
  }

  _renderSelect = (options, selectedOption, onChange) => {
    return (
      <UnsortedSelect
        value={selectedOption}
        options={options}
        components={{ Option: SelectOption, Input: SelectInput }}
        isClearable={false}
        isSearchable={true}
        backspaceRemovesValue={false}
        maxMenuHeight={300}
        styles={{
          ...recipeBuilderSelectStyles,
          groupHeading: (styles) => ({
            ...styles,
            color: '#ff7436',
          }),
          container: (styles) => ({
            ...styles,
            flexGrow: 1,
          }),
        }}
        onChange={onChange}
      />
    );
  };

  _handleCodeTypeChanged = (option) => {
    const {
      data: { type, codeLanguage },
      onChangeRecipeGroup,
    } = this.props;
    const value = getNewSelectionValue(option, true); // code lang or tx id

    if (option.type === 'custom_code') {
      if (option.type !== type || value !== codeLanguage) {
        const newData = {
          type: option.type,
          codeLanguage: value,
          code: Constants.DefaultCode[value] || '',
        };

        onChangeRecipeGroup(newData);
      }
    } else {
      const newData = {
        type: 'reusable_tx',
        transformId: value,
      };

      onChangeRecipeGroup(newData);
    }
  };

  _handleAggregationWindowChanged = (option) => {
    const { data, onChangeRecipeGroup } = this.props;
    const value = getNewSelectionValue(option, true);

    onChangeRecipeGroup({
      ...data,
      options: {
        ...data?.options,
        aggregation_window: value,
      },
    });
  };

  _handleCodeChanged = (code) => {
    const { data, onChangeRecipeGroup } = this.props;
    const newData = {
      ...data,
      code,
    };

    onChangeRecipeGroup(newData);
  };
}

function mapStateToProps(state, ownProps) {
  const {
    entities: { transforms },
    transforms: {
      allTransforms: { decoded: decodedTransforms },
      reusableRecordTransforms: {
        sortedByNameIds,
        sortedByNamePublicIds,
        lastUpdated: reusableTransformsLastUpdated,
      },
    },
    request,
  } = state;
  const { languageOptions } = ownProps;
  const languages = map(languageOptions, 'value');

  function filterTransform(transformId) {
    const { code_type, output_type } = transforms[transformId] || {};

    return languages.includes(code_type) && output_type === TransformType.RECORD;
  }

  function getTransformOption(transformId) {
    const { name, description } = transforms[transformId];

    return {
      value: transformId,
      label: name,
      description: description,
    };
  }

  const transformOptions = map(filter(sortedByNameIds, filterTransform), getTransformOption);
  const publicTransformOptions = map(
    filter(sortedByNamePublicIds, filterTransform),
    getTransformOption,
  );
  const isLoadingReusableTransforms = !!(
    request[GET_CODE_CONTAINERS_REQUEST] || request[GET_PUBLIC_CODE_CONTAINERS_REQUEST]
  );

  const isLoadingTransformedSamples = !!request[GET_TRANSFORMED_SAMPLES_REQUEST];

  return {
    transforms,
    decodedTransforms,
    transformOptions,
    publicTransformOptions,
    reusableTransformsLoaded: !!reusableTransformsLastUpdated,
    isLoadingReusableTransforms,
    isLoadingTransformedSamples,
  };
}

export default connect(mapStateToProps)(TransformCodeRecipeGroup);
